import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, apiBase, ApiError } from "../api/client";

/**
 * Schlankes Feedback-Widget: ein dezenter Header-Button, ein Modal zum Melden
 * von Fehlern/Wünschen und ein Erfolgs-Toast. Das Feedback landet über
 * POST /api/feedback/submit im Taskmanagement und wird von dort automatisch
 * (InfiniLoop/Slack) weiterverarbeitet — es gibt bewusst KEINE Board-/Listen-UI
 * im Client. Screenshots gehen optional vorab an POST /api/feedback/upload.
 */

type FeedbackType = "bug" | "feature" | "improvement" | "idea";

const TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: "bug", label: "Fehler / Bug" },
  { value: "feature", label: "Feature-Wunsch" },
  { value: "improvement", label: "Verbesserung" },
  { value: "idea", label: "Idee" },
];

interface UploadResponse {
  ok: boolean;
  screenshot_path?: string;
}

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>("bug");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [file, setFile] = useState<File | null>(null);
  // Honeypot: bleibt leer; nur Bots füllen es aus (siehe feedback.ts).
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);

  const firstFieldRef = useRef<HTMLSelectElement | null>(null);

  // Escape schließt das Modal; Fokus auf das erste Feld beim Öffnen.
  useEffect(() => {
    if (!open) return;
    firstFieldRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Erfolgs-Toast nach 5s automatisch ausblenden.
  useEffect(() => {
    if (!showToast) return;
    const t = window.setTimeout(() => setShowToast(false), 5000);
    return () => window.clearTimeout(t);
  }, [showToast]);

  function resetForm() {
    setType("bug");
    setTitle("");
    setMessage("");
    setName("");
    setEmail("");
    setFile(null);
    setWebsite("");
    setError(null);
  }

  async function uploadScreenshot(f: File): Promise<string | undefined> {
    const form = new FormData();
    form.append("screenshot", f);
    // Kein Content-Type setzen — der Browser ergänzt die multipart-Boundary.
    const res = await fetch(`${apiBase}/api/feedback/upload`, {
      method: "POST",
      credentials: "include",
      body: form,
    });
    if (!res.ok) {
      let msg = `Upload fehlgeschlagen (HTTP ${res.status})`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) msg = body.error;
      } catch {
        // keine JSON-Antwort — generische Meldung behalten
      }
      throw new ApiError(res.status, msg);
    }
    const data = (await res.json()) as UploadResponse;
    return data.screenshot_path;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setError(null);

    if (!title.trim()) {
      setError("Bitte gib einen Titel an.");
      return;
    }
    if (!message.trim()) {
      setError("Bitte beschreibe dein Feedback.");
      return;
    }

    setBusy(true);
    try {
      let screenshotPath: string | undefined;
      if (file) screenshotPath = await uploadScreenshot(file);

      await api.post("/api/feedback/submit", {
        type,
        title: title.trim(),
        message: message.trim(),
        reporter_name: name.trim() || undefined,
        reporter_email: email.trim() || undefined,
        screenshot_path: screenshotPath,
        page_url: window.location.href,
        website, // Honeypot
      });

      resetForm();
      setOpen(false);
      setShowToast(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Übermittlung fehlgeschlagen. Bitte später erneut versuchen.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => setOpen(true)}
        title="Feedback geben — Fehler melden oder Wunsch äußern"
      >
        Feedback
      </button>

      {open && (
        <div
          className="fb-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fb-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="fb-card">
            <div className="fb-card-header">
              <h2 id="fb-title" className="fb-title">Feedback geben</h2>
              <button
                type="button"
                className="fb-close"
                aria-label="Schließen"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </div>
            <p className="fb-subtitle">
              Fehler, Verbesserung oder Idee? Dein Feedback wird automatisch
              bearbeitet.
            </p>

            <form className="fb-form" onSubmit={handleSubmit} noValidate>
              <div className="form-row">
                <label htmlFor="fb-type">Art</label>
                <select
                  id="fb-type"
                  ref={firstFieldRef}
                  value={type}
                  onChange={(e) => setType(e.target.value as FeedbackType)}
                >
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label htmlFor="fb-title-input">Titel</label>
                <input
                  id="fb-title-input"
                  type="text"
                  required
                  maxLength={200}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Kurz und knapp"
                />
              </div>

              <div className="form-row">
                <label htmlFor="fb-message">Beschreibung</label>
                <textarea
                  id="fb-message"
                  required
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Was ist passiert bzw. was wünschst du dir?"
                />
              </div>

              <div className="form-inline-group">
                <div className="form-row">
                  <label htmlFor="fb-name">Name (optional)</label>
                  <input
                    id="fb-name"
                    type="text"
                    maxLength={120}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="form-row">
                  <label htmlFor="fb-email">E-Mail (optional)</label>
                  <input
                    id="fb-email"
                    type="email"
                    maxLength={200}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="für Rückmeldung per E-Mail"
                  />
                </div>
              </div>

              <div className="form-row">
                <label htmlFor="fb-screenshot">Screenshot (optional)</label>
                <div className="upload-area">
                  <input
                    id="fb-screenshot"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                  <span className="upload-hint">
                    {file ? file.name : "Max. 5 MB — PNG, JPG, GIF oder WebP"}
                  </span>
                </div>
              </div>

              {/* Honeypot: für Menschen unsichtbar, nur Bots füllen es aus. */}
              <input
                type="text"
                name="website"
                className="fb-honeypot"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />

              {error && <div className="fb-error" role="alert">{error}</div>}

              <div className="fb-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setOpen(false)}
                  disabled={busy}
                >
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? "Wird gesendet…" : "Absenden"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showToast && (
        <div className="fb-toast" role="status">
          Danke! Dein Feedback wurde übermittelt und wird automatisch bearbeitet.
        </div>
      )}
    </>
  );
}
